import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth.store';
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  getPreferences,
  updatePreferences,
  Notification,
  UpdatePreferencesRequest,
} from '../lib/notifications-api';

/**
 * Hook for fetching paginated notifications
 */
export function useNotifications(limit: number = 20) {
  const { accessToken } = useAuthStore();

  return useInfiniteQuery({
    queryKey: ['notifications'],
    queryFn: async ({ pageParam }) => {
      if (!accessToken) throw new Error('Not authenticated');
      return getNotifications(accessToken, pageParam, limit);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!accessToken,
  });
}

/**
 * Hook for fetching unread count
 */
export function useUnreadCount() {
  const { accessToken } = useAuthStore();

  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      if (!accessToken) throw new Error('Not authenticated');
      return getUnreadCount(accessToken);
    },
    enabled: !!accessToken,
    refetchInterval: 30000, // Fallback polling every 30s
  });
}

/**
 * Hook for marking a notification as read
 */
export function useMarkAsRead() {
  const { accessToken } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      if (!accessToken) throw new Error('Not authenticated');
      await markAsRead(accessToken, notificationId);
      return notificationId;
    },
    onSuccess: (notificationId) => {
      // Optimistically update the notification in cache
      queryClient.setQueryData<{ pages: { data: Notification[] }[] }>(
        ['notifications'],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((n) =>
                n.notificationId === notificationId
                  ? { ...n, isRead: true, readAt: new Date().toISOString() }
                  : n,
              ),
            })),
          };
        },
      );
      // Invalidate unread count
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });
}

/**
 * Hook for marking all notifications as read
 */
export function useMarkAllAsRead() {
  const { accessToken } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('Not authenticated');
      return markAllAsRead(accessToken);
    },
    onSuccess: () => {
      // Mark all as read in cache
      queryClient.setQueryData<{ pages: { data: Notification[] }[] }>(
        ['notifications'],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((n) => ({
                ...n,
                isRead: true,
                readAt: n.readAt || new Date().toISOString(),
              })),
            })),
          };
        },
      );
      // Set unread count to 0
      queryClient.setQueryData(['notifications', 'unread-count'], { count: 0 });
    },
  });
}

/**
 * Hook for fetching notification preferences
 */
export function usePreferences() {
  const { accessToken } = useAuthStore();

  return useQuery({
    queryKey: ['notification-preferences'],
    queryFn: async () => {
      if (!accessToken) throw new Error('Not authenticated');
      return getPreferences(accessToken);
    },
    enabled: !!accessToken,
  });
}

/**
 * Hook for updating notification preferences
 */
export function useUpdatePreferences() {
  const { accessToken } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdatePreferencesRequest) => {
      if (!accessToken) throw new Error('Not authenticated');
      return updatePreferences(accessToken, data);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['notification-preferences'], data);
    },
  });
}
